import { initials } from '../lib/format.ts';
import { useStore } from '../lib/store.ts';
import { app } from '../state/app.ts';
import { chatStore } from '../state/chat.ts';
import { runningChatCount } from '../state/chatTurns.ts';
import { selectTasks, tasksStore } from '../state/tasks.ts';
import { Icon, JeromeMark } from '../ui/Icon.tsx';
import { useVoiceCapability, VOICE_OFF_TITLE } from '../voice/capability.ts';
import { showVoiceError, VoiceError } from '../voice/errors.ts';

// Bovenbalk op de telefoon: merkteken met de naam van de functie, rechts de microfoon van de spraakmodus,
// de werkers (met het aantal bezig; oranje uitroepteken als Jerome op een akkoord wacht) en de avatar die
// het vel Meer opent. De microfoon staat hier omdat de onderste navigatie met Spreek verdwijnt zodra een
// mail of gesprek open staat; zo is de spraakmodus altijd bereikbaar (de oranje dicteerknop in de chat
// transcribeert alleen). Zonder ElevenLabs-sleutel is hij gedempt en geeft een tik één melding.
// Boven de balk blijft ruimte voor de notch en de statusbalk (pt-safe). De balk is donker (Hero-navy):
// als webapp op het beginscherm tekent iOS de statusbalk (klok, batterij) in wit over die ruimte heen
// (`black-translucent` in index.html), en op een lichte balk was die onleesbaar.
// De bliksem telt ook de gesprekken waar Jerome mee bezig is (ticket 30961, deel 2): in het vel Werkers staat elk als rij.
// Gevlagd (ticket 30961): de lijst van alle gevlagde mails, op de desktop de kolom helemaal links, staat hier als vel achter de
// vlag, met het aantal gevlagde mails volgens de server (zoals Gevlagd in de filterbalk); een tik op een mail opent het gesprek
// over die mail.

export function PhoneTopBar(props: { title: string; onWorkers: () => void; workersOpen: boolean; onFlagged?: (() => void) | undefined; flaggedOpen?: boolean | undefined; onMore: () => void; moreOpen: boolean; onVoice: () => void; voiceActive: boolean }) {
  const me = useStore(app, (s) => s.me);
  const newVersion = useStore(app, (s) => s.newVersion);
  const wsStatus = useStore(app, (s) => s.wsStatus);
  // De taken en (ticket 30961, deel 2) de gesprekken waar Jerome mee bezig is, zoals de werkerskolom in het vel ze toont.
  const tasksBusy = useStore(tasksStore, (s) => selectTasks(s, { showAll: false }).length);
  const chatsBusy = useStore(chatStore, (s) => runningChatCount(s.chats));
  const busy = tasksBusy + chatsBusy;
  const attention = useStore(tasksStore, (s) => s.permissions.length > 0);
  const flagged = useStore(tasksStore, (s) => (s.countsLoaded ? s.counts.flagged : 0));
  const voice = useVoiceCapability();
  const voiceMuted = !voice.dictation;
  return (
    <header className="shrink-0 pt-safe bg-bar text-white">
      <div className="h-12 flex items-center gap-1 pl-3 pr-2">
        <JeromeMark size={28} tone="inverse" />
        <span className="font-display text-[19px] truncate flex-1 ml-1">{props.title}</span>
        {wsStatus !== 'open' ? <span className="h-2 w-2 rounded-full bg-amber shrink-0" title={wsStatus === 'verbinden' ? 'Verbinden met Jerome…' : 'Geen verbinding met Jerome'} /> : null}
        <button
          type="button"
          aria-label="Spraakmodus"
          aria-pressed={props.voiceActive}
          aria-disabled={voiceMuted || undefined}
          title={voiceMuted ? VOICE_OFF_TITLE : undefined}
          onClick={voiceMuted ? () => showVoiceError(new VoiceError('off'), 'dictation') : props.onVoice}
          className={`h-11 w-11 rounded-md flex items-center justify-center active:bg-white/10 ${voiceMuted ? 'opacity-50' : ''}`}
        >
          <span className={`h-9 w-9 rounded-full flex items-center justify-center ${props.voiceActive ? 'bg-white text-amber ring-2 ring-amber' : 'bg-amber text-white shadow-card'}`}>
            <Icon name="mic" size={20} />
          </span>
        </button>
        {props.onFlagged ? (
          <button type="button" aria-label="Gevlagde mails" aria-pressed={props.flaggedOpen ?? false} onClick={props.onFlagged} className="relative h-11 w-11 rounded-md flex items-center justify-center text-white/80 active:bg-white/10">
            <Icon name="flag" size={21} className={flagged ? 'text-white' : ''} />
            {flagged ? (
              <span data-count="flagged" className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold text-white flex items-center justify-center bg-red ring-1 ring-white/60" title={`${flagged} gevlagd`}>
                {flagged}
              </span>
            ) : null}
          </button>
        ) : null}
        <button type="button" aria-label="Werkers" aria-pressed={props.workersOpen} onClick={props.onWorkers} className="relative h-11 w-11 rounded-md flex items-center justify-center text-white/80 active:bg-white/10">
          <Icon name="bolt" size={22} className={busy || attention ? 'text-white' : ''} />
          {attention || busy ? (
            <span className={`absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-semibold text-white flex items-center justify-center ${attention ? 'bg-amber' : 'bg-accent ring-1 ring-white/60'}`} title={attention ? 'Jerome wacht op jouw akkoord' : `${busy} bezig`}>
              {attention ? '!' : busy}
            </span>
          ) : null}
        </button>
        <button type="button" aria-label="Profielmenu" aria-expanded={props.moreOpen} onClick={props.onMore} title={me?.name} className="relative ml-1 h-10 w-10 rounded-full bg-white/15 text-white font-semibold text-sm flex items-center justify-center">
          {initials(me?.name ?? '') || <Icon name="user" size={18} />}
          {newVersion ? <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-amber ring-2 ring-bar" title="Nieuwe versie beschikbaar" /> : null}
        </button>
      </div>
    </header>
  );
}
